import { useState } from 'react';
import type { BufferedReaction } from '../../shared/bufferedReaction';
import type { IconDefinition } from '../../shared/iconDefinition';
import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';
import { ApiRequestError } from '../api/apiRequestError';
import { getReviewerReplayStream } from '../api/getReviewerReplayStream';
import { formatVideoClock } from '../formatVideoClock';
import { TwemojiGlyphImage } from '../twemojiGlyphImage';

// Shows the reviewer their OWN trail, and nobody else's until they ask.
//
// The replay offer below is the payoff — the thing that makes someone forward the
// link — and it is safe here for one specific reason: this reviewer has finished,
// so their reactions are already recorded and immutable and can no longer be
// influenced by seeing anyone else's. The server enforces that independently
// (403 until finished_at is set); this screen is not the gate, it just respects it.
//
// The fetch below is the ONLY place the client requests a replay stream during a
// reviewer's session, and it happens on click, not on mount. Prefetching it —
// even invisibly, even "to make the button feel fast" — would put other people's
// reactions in the browser during the watch and defeat the whole mechanism.
// Below this, a reviewer is genuinely early and the replay they are about to see
// will be thin. Above it, saying so would be false modesty.
const EARLY_REVIEWER_THRESHOLD = 5;

export function ViewerSummaryScreen(input: {
  active: boolean;
  mode: 'blind' | 'live';
  finishedCount: number;
  icons: IconDefinition[];
  reactions: BufferedReaction[];
  durationMs: number;
  furthestMs: number;
  reviewerId: string | null;
  onOpenReplay: (events: ReplayReactionEvent[]) => void;
}) {
  const [loadingReplay, setLoadingReplay] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);

  const iconById = new Map(input.icons.map((icon) => [icon.id, icon]));
  const distinctKinds = new Set(input.reactions.map((reaction) => reaction.icon_id)).size;
  const watchedPct = input.durationMs > 0
    ? Math.min(100, Math.round((input.furthestMs / input.durationMs) * 100))
    : 0;

  const openReplay = async () => {
    if (!input.reviewerId || loadingReplay) return;
    setLoadingReplay(true);
    setReplayError(null);
    try {
      input.onOpenReplay(await getReviewerReplayStream(input.reviewerId));
    } catch (caught) {
      setReplayError(caught instanceof ApiRequestError ? caught.message : 'Could not load the replay.');
    } finally {
      setLoadingReplay(false);
    }
  };

  return (
    <section className={input.active ? 'screen screenOn screenScroll' : 'screen'}>
      <div className="pageWrap">
        <h1>Your reaction trail</h1>
        <p className="pageSub">
          {input.reactions.length > 0
            ? 'Only you see this. The creator only ever sees the combined shape across everyone.'
            : 'You did not tap anything — which is itself a result worth writing down.'}
        </p>

        <div className="statRow">
          <div>
            <b>{input.reactions.length}</b>taps
          </div>
          <div>
            <b>{distinctKinds}</b>different feelings
          </div>
          <div>
            <b>{watchedPct}%</b>watched
          </div>
        </div>

        <div className="trailStrip">
          {input.reactions.map((reaction, index) => {
            const icon = iconById.get(reaction.icon_id);
            if (!icon) return null;
            const leftPct = input.durationMs > 0 ? (100 * reaction.video_time_ms) / input.durationMs : 0;
            return (
              <span className="trailMark" key={`${reaction.client_seq}-${index}`} style={{ left: `${leftPct.toFixed(2)}%` }}>
                <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={22} />
              </span>
            );
          })}
        </div>
        <div className="trailAxis">
          <span>0:00</span>
          <span>{formatVideoClock(input.durationMs)}</span>
        </div>

        <table className="trailTable">
          <tbody>
            {input.reactions.length === 0 ? (
              <tr>
                <td colSpan={3} className="tableEmpty">
                  No taps recorded.
                </td>
              </tr>
            ) : (
              input.reactions.map((reaction, index) => {
                const icon = iconById.get(reaction.icon_id);
                if (!icon) return null;
                return (
                  <tr key={`${reaction.client_seq}-${index}`}>
                    <td>
                      <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={20} />
                    </td>
                    <td>{icon.label}</td>
                    <td>
                      {formatVideoClock(reaction.video_time_ms)} · {reaction.method}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Cowork016 §3, corrected by Cowork018: BLIND TESTS ONLY. On a live test
            they already saw everyone's reactions while watching, so promising a
            payoff that has already been delivered would be wrong and slightly
            insulting. Conditioned on mode as well as on the count. */}
        {input.mode === 'blind' && input.finishedCount < EARLY_REVIEWER_THRESHOLD ? (
          <div className="callout">
            <b>You're one of the first to react to this.</b> Come back later and you'll see
            how everyone else felt — there will be more to see once other people have
            watched.
          </div>
        ) : null}

        <div className="callout">
          <b>See what everyone else felt.</b>{' '}
          {input.mode === 'live'
            ? 'You saw these while you watched — this plays them back from the start.'
            : "Now that your own reactions are in, you can watch it back with everyone's reactions floating over the video. We keep this until afterwards on purpose — seeing other people's first would change yours."}
        </div>

        <button className="buttonPrimary" type="button" onClick={() => void openReplay()} disabled={loadingReplay}>
          {loadingReplay ? 'Loading…' : 'See what everyone else felt'}
        </button>

        {replayError ? <div className="calloutError">{replayError}</div> : null}
      </div>
    </section>
  );
}
